import type { OfflineDatabaseSqlStatement } from './offlineDatabase.statements';
import { jsonToSqlValue } from './offlineDatabase.serialization';
import {
  addDefinedSqlUpdate,
  addSqlUpdate,
  buildSqlUpdate,
  createSqlUpdateBuilder,
} from './offlineDatabase.sqlUpdates';

type TimestampFactory = () => string;

const isoNow = (): string => new Date().toISOString();

export type OfflineItemSyncStatus = 'pending' | 'syncing' | 'synced' | 'failed';

export interface OfflineOnDemandItemInsertInput {
  environmentKey: string;
  tempId: string;
  reviewId: number;
  name: string;
  description?: string;
  categoryId?: number;
  questionsSchema?: any;
  syncStatus: string;
}

export interface OfflineOnDemandItemUpdateInput {
  name?: string;
  description?: string;
  category_id?: number;
  questions_schema?: any;
}

export const buildOfflineOnDemandItemInsertStatement = (
  item: OfflineOnDemandItemInsertInput,
  getTimestamp: TimestampFactory = isoNow
): OfflineDatabaseSqlStatement => ({
	  sql: `INSERT INTO offline_on_demand_items
	           (environment_key, temp_id, review_id, name, description, category_id, questions_schema, sync_status, created_at)
	           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
	  params: [
	    item.environmentKey,
	    item.tempId,
    item.reviewId,
    item.name,
    item.description || null,
    item.categoryId || null,
    jsonToSqlValue(item.questionsSchema),
    item.syncStatus,
    getTimestamp(),
  ],
});

export const buildOfflineItemSyncStatusUpdateStatement = (
  tempId: string,
  syncStatus: OfflineItemSyncStatus,
  syncError?: string,
  realId?: number,
  environmentKey?: string,
  getTimestamp: TimestampFactory = isoNow
): OfflineDatabaseSqlStatement => {
  const updateBuilder = createSqlUpdateBuilder();

  addSqlUpdate(updateBuilder, 'sync_status', syncStatus);
  addDefinedSqlUpdate(updateBuilder, 'sync_error', syncError);
  if (syncStatus === 'synced') {
    addSqlUpdate(updateBuilder, 'synced_at', getTimestamp());
  }

  if (realId !== undefined && syncStatus === 'synced') {
    addSqlUpdate(updateBuilder, 'temp_id', `synced_${realId}`);
  }

  return buildSqlUpdate(
    'offline_on_demand_items',
    updateBuilder,
    `temp_id = ?${environmentKey ? ' AND environment_key = ?' : ''}`,
    environmentKey ? [tempId, environmentKey] : [tempId]
  )!;
};

export const buildPendingOfflineOnDemandItemsSelectStatement = (): OfflineDatabaseSqlStatement => ({
  sql: 'SELECT * FROM offline_on_demand_items WHERE sync_status IN (?, ?, ?) ORDER BY created_at',
  params: ['pending', 'failed', 'syncing'],
});

export const buildPendingOfflineOnDemandItemsForEnvironmentSelectStatement = (
  environmentKey: string
): OfflineDatabaseSqlStatement => ({
  sql: 'SELECT * FROM offline_on_demand_items WHERE sync_status IN (?, ?, ?) AND environment_key = ? ORDER BY created_at',
  params: ['pending', 'failed', 'syncing', environmentKey],
});

export const buildOfflineOnDemandItemSelectStatement = (
  itemId: number | string,
  environmentKey?: string
): OfflineDatabaseSqlStatement => ({
  sql: `SELECT * FROM offline_on_demand_items WHERE (id = ? OR temp_id LIKE ?)${environmentKey ? ' AND environment_key = ?' : ''}`,
  params: environmentKey ? [itemId, `%${itemId}%`, environmentKey] : [itemId, `%${itemId}%`],
});

export const buildOfflineOnDemandItemsByReviewSelectStatement = (
  reviewId: number,
  environmentKey?: string
): OfflineDatabaseSqlStatement => ({
  sql: `SELECT * FROM offline_on_demand_items WHERE review_id = ?${environmentKey ? ' AND environment_key = ?' : ''}`,
  params: environmentKey ? [reviewId, environmentKey] : [reviewId],
});

export const buildOfflineOnDemandItemDeleteStatement = (
  itemIdOrTempId: number | string
): OfflineDatabaseSqlStatement => ({
  sql: 'DELETE FROM offline_on_demand_items WHERE id = ? OR temp_id = ? OR temp_id = ?',
  params: [itemIdOrTempId, itemIdOrTempId, `synced_${itemIdOrTempId}`],
});

export const buildOfflineOnDemandItemUpdateStatement = (
  itemId: number | string,
  updates: OfflineOnDemandItemUpdateInput,
  environmentKey?: string,
  getTimestamp: TimestampFactory = isoNow
): OfflineDatabaseSqlStatement => {
  const updateBuilder = createSqlUpdateBuilder();

  addDefinedSqlUpdate(updateBuilder, 'name', updates.name);
  addDefinedSqlUpdate(updateBuilder, 'description', updates.description);
  addDefinedSqlUpdate(updateBuilder, 'category_id', updates.category_id);
  addDefinedSqlUpdate(updateBuilder, 'questions_schema', updates.questions_schema, JSON.stringify);

  addSqlUpdate(updateBuilder, 'sync_status', 'pending');
  addSqlUpdate(updateBuilder, 'sync_error', null);
  addSqlUpdate(updateBuilder, 'updated_at', getTimestamp());

	  return buildSqlUpdate(
	    'offline_on_demand_items',
	    updateBuilder,
	    `(id = ? OR temp_id LIKE ?)${environmentKey ? ' AND environment_key = ?' : ''}`,
	    environmentKey ? [itemId, typeof itemId === 'string' ? itemId : `%${itemId}%`, environmentKey] : [itemId, typeof itemId === 'string' ? itemId : `%${itemId}%`]
	  )!;
};
